import { Link } from 'react-router'
import { DisplayHeading } from '@carouselgg/nexus/display-heading'
import { GradientText } from '@carouselgg/nexus/gradient-text'
import { TiltPanel } from '@carouselgg/nexus/tilt-panel'
import { MarqueeRing } from '@carouselgg/nexus/marquee-ring'
import { TrustGauge } from '@carouselgg/nexus/trust-gauge'
import {
  DiscordLogo, RiotLogo, SteamLogo,
  XboxLogo, PlayStationLogo, TwitchLogo, YouTubeLogo,
} from '@carouselgg/nexus/icon'
import { useTrackVisibility } from '../hooks/useTrackVisibility'
import { demoApplicants } from './demoData'
import styles from './Hero.module.css'

const platformIcons = [
  { Icon: RiotLogo, label: 'Riot Games' },
  { Icon: SteamLogo, label: 'Steam' },
  { Icon: DiscordLogo, label: 'Discord' },
  { Icon: XboxLogo, label: 'Xbox' },
  { Icon: PlayStationLogo, label: 'PlayStation' },
  { Icon: TwitchLogo, label: 'Twitch' },
  { Icon: YouTubeLogo, label: 'YouTube' },
]

const GAUGE_TONE = {
  good: { from: 'var(--nexus-green)', to: 'var(--nexus-green)' },
  bad: { from: 'var(--nexus-pink)', to: 'var(--nexus-pink)' },
}

/** Compact radar, drawn inline — the Trust Score section uses the full chart. */
function EvidenceSpark() {
  const [, smurf] = demoApplicants
  const axes = smurf.radar
  const points = axes
    .map((point, index) => {
      const angle = (Math.PI * 2 * index) / axes.length - Math.PI / 2
      const radius = (point.value / 100) * 44
      return `${60 + Math.cos(angle) * radius},${46 + Math.sin(angle) * radius}`
    })
    .join(' ')

  return (
    <svg viewBox="0 0 120 92" className={styles.evidenceChart} aria-hidden="true">
      <polygon
        points="60,4 108,28 108,64 60,88 12,64 12,28"
        fill="none"
        stroke="var(--nexus-border)"
        strokeWidth="1"
      />
      <polygon
        points="60,26 84,38 84,58 60,70 36,58 36,38"
        fill="none"
        stroke="var(--nexus-border-subtle)"
        strokeWidth="1"
      />
      <polygon
        points={points}
        fill="color-mix(in srgb, var(--nexus-pink) 18%, transparent)"
        stroke="var(--nexus-pink)"
        strokeWidth="1.6"
      />
      <polygon
        points="60,24 81,36 81,56 60,68 39,56 39,36"
        fill="none"
        stroke="var(--nexus-border-strong)"
        strokeWidth="1.2"
        strokeDasharray="4 3"
      />
    </svg>
  )
}

export function Hero() {
  const ref = useTrackVisibility<HTMLElement>('hero')

  return (
    <section id="hero" className={styles.hero} ref={ref}>
      <div className={styles.inner}>
        <div className={styles.copy}>
          <p className={styles.eyebrow}>
            <span className={styles.seed}>01</span>
            <span className={styles.rule} />
            Organized amateur league play
          </p>

          {/* The space before the <br> is load-bearing, not a typo: <br>
              contributes no character to textContent, so without it the DOM
              reads "Esports forEveryone." — which breaks the landing e2e
              assertion and makes screen readers run the words together.
              Uppercase is presentational only. */}
          <DisplayHeading as="h1" size="7xl">
            Esports for{' '}
            <br />
            <GradientText gradient="brand" angle={115}>everyone.</GradientText>
          </DisplayHeading>

          {/* Three ideas, in one causal chain rather than stapled together:
              everyone can play (leagues at your own Elo) — which only works if
              the bracket is honest — which is why identity is verified and
              scored. Tournaments are supported too, so this must not read as
              anti-tournament; the league is the differentiator, not the enemy. */}
          <p className={styles.subline}>
            Play a full season against teams at your own Elo — leagues, not just tournaments. That
            only works if everyone is who they say they are, so every player carries a verified
            identity and a Trust Score that keeps smurfs out of your bracket.
          </p>

          <div className={styles.ctas}>
            <Link to="/compete" className={styles.primaryCta}>Compete</Link>
            <Link to="/create-org?type=league" className={styles.secondaryCta}>Start a League</Link>
          </div>

          <div className={styles.platforms}>
            <span className={styles.platformsLabel}>Identity verified across</span>
            <div className={styles.platformIcons}>
              {platformIcons.map(({ Icon, label }) => (
                <Icon key={label} size={19} aria-label={label} />
              ))}
            </div>
          </div>
        </div>

        <div className={styles.stage}>
          <MarqueeRing
            className={styles.ring}
            size={340}
            duration={46}
            text="VERIFIED IDENTITY · REAL COMPETITION · FAIR PLAY · "
          />
          <TiltPanel className={styles.panel} label="Registration review — Spring Split">
            <div className={styles.applicants}>
              {demoApplicants.map((applicant) => (
                <div
                  key={applicant.id}
                  className={`${styles.applicant} ${styles[applicant.tone]}`}
                >
                  <TrustGauge
                    score={applicant.score}
                    size={56}
                    gradient={GAUGE_TONE[applicant.tone]}
                    title={`Trust Score ${applicant.score} — ${applicant.level}`}
                  />
                  <div className={styles.applicantBody}>
                    <span className={styles.handle}>{applicant.handle}</span>
                    <span className={styles.detail}>{applicant.detail}</span>
                  </div>
                  <span className={styles.verdict}>{applicant.verdict}</span>
                </div>
              ))}
            </div>

            <div className={styles.evidence}>
              <EvidenceSpark />
              <div className={styles.evidenceText}>
                <span className={styles.evidenceLabel}>Why this score</span>
                <p className={styles.evidenceBody}>{demoApplicants[1].evidence}</p>
              </div>
            </div>
          </TiltPanel>
        </div>
      </div>
    </section>
  )
}
